import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FixedAssetDetail } from '@/hooks/useFixedAssetDetail';
import { computeNetRent } from '@/lib/utils/fixed-asset-utils';

interface AssetTypeSpecificInfoProps {
  detail: FixedAssetDetail;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 py-2 border-b last:border-0">
      <span className="text-sm text-muted-foreground sm:w-48 shrink-0">{label}</span>
      <span className="text-sm font-medium">{value ?? '—'}</span>
    </div>
  );
}

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

export function AssetTypeSpecificInfo({ detail }: AssetTypeSpecificInfoProps) {
  const { asset, apartment, land, house } = detail;

  if (asset.asset_type === 'apartment' && apartment) {
    const managementFee = Number(apartment.management_fee) || 0;
    const managementFeeVat = Number(apartment.management_fee_vat) || 0;
    const netRent = computeNetRent(
      Number(apartment.expected_rent),
      Number(apartment.service_fee),
      managementFee,
      managementFeeVat
    );
    return (
      <Card>
        <CardHeader>
          <CardTitle>Apartment Details</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRow label="Apartment Number" value={apartment.apartment_number} />
          <DetailRow
            label="Expected Rent"
            value={formatCurrency(Number(apartment.expected_rent), asset.currency)}
          />
          <DetailRow
            label="Service Fee"
            value={formatCurrency(Number(apartment.service_fee), asset.currency)}
          />
          <DetailRow
            label="Management Fee"
            value={managementFee ? formatCurrency(managementFee, asset.currency) : '—'}
          />
          <DetailRow
            label="Management Fee VAT"
            value={managementFeeVat ? formatCurrency(managementFeeVat, asset.currency) : '—'}
          />
          <DetailRow
            label="Net Rent"
            value={
              <span data-testid="net-rent">
                {formatCurrency(netRent, asset.currency)}
              </span>
            }
          />
          <DetailRow label="Rent Day of Month" value={apartment.rent_day_of_month} />
          <DetailRow
            label="Occupied"
            value={
              <Badge variant={apartment.is_occupied ? 'default' : 'outline'}>
                {apartment.is_occupied ? 'Occupied' : 'Vacant'}
              </Badge>
            }
          />
        </CardContent>
      </Card>
    );
  }

  if (asset.asset_type === 'land' && land) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Land Details</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRow label="Acres" value={Number(land.acres).toLocaleString()} />
        </CardContent>
      </Card>
    );
  }

  if (asset.asset_type === 'house') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>House Details</CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRow
            label="Linked Land"
            value={
              house?.linkedLandName
                ? house.linkedLandName
                : <span data-testid="no-linked-land">No linked land</span>
            }
          />
        </CardContent>
      </Card>
    );
  }

  // No type-specific info for other types
  return null;
}
